import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { CheckCircle2, XCircle, RotateCcw, Clock, FileText, Video, PlayCircle, Image as ImageIcon, ArrowLeft } from 'lucide-react';

import {
  getTraining,
  listTrainingContent,
  getTrainingApprovalSteps,
  approveTraining,
  requestTrainingRevision,
  rejectTraining,
  getTrainingQuizConfig,
  listTrainingQuizQuestions,
} from '../../services/trainingService';
import { formatDate } from '../../utils/Utils';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Tabs from '../../components/ui/Tabs';
import ContentPlayer from '../my-trainings/player/ContentPlayer';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const InfoCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 14px 20px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const TrainingTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: #FEF3C7;
  color: #92400E;
`;

const Section = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const SectionTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 12px;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${({ $active }) => $active ? '#0291EF' : '#E0E4E8'};
  border-radius: 6px;
  margin-bottom: 6px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  cursor: pointer;
  background: ${({ $active }) => $active ? '#EFF6FF' : '#fff'};
  &:hover { border-color: #0291EF; }
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  background: #E0E7FF;
  color: #3730A3;
  text-transform: uppercase;
`;

const PlayerArea = styled.div`
  margin-top: 12px;
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  overflow: hidden;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #F1F3F5;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  &:last-child { border-bottom: none; }
`;

const StepIndex = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  min-width: 20px;
`;

const DecisionBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $d }) =>
    $d === 'approved' ? '#DCFCE7' :
    $d === 'revision_requested' ? '#FEF3C7' :
    $d === 'rejected' ? '#FEE2E2' : '#E0E7FF'};
  color: ${({ $d }) =>
    $d === 'approved' ? '#166534' :
    $d === 'revision_requested' ? '#92400E' :
    $d === 'rejected' ? '#991B1B' : '#3730A3'};
`;

const QuestionCard = styled.div`
  border-left: 3px solid ${({ $color }) => $color || '#0291EF'};
  padding: 12px 16px;
  margin-bottom: 8px;
  background: #FAFBFC;
  border-radius: 0 6px 6px 0;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #E0E4E8;
`;

const CONTENT_ICONS = {
  video: Video, youtube: PlayCircle, vimeo: PlayCircle,
  pdf: FileText, pptx: FileText, image: ImageIcon,
};

const typeColors = {
  singleChoice: '#2563eb', singleChoiceAlternate: '#3b82f6',
  multipleChoice: '#16a34a', multipleChoiceAlternate: '#ca8a04',
  ranking: '#dc2626', freeText: '#9333ea',
};

export default function TrainingReviewPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const history = useHistory();

  const [training, setTraining] = useState(null);
  const [content, setContent] = useState([]);
  const [steps, setSteps] = useState([]);
  const [quizConfig, setQuizConfig] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeContent, setActiveContent] = useState(null);
  const [comment, setComment] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('content');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getTraining(id).then((r) => r.data?.data),
      listTrainingContent(id).then((r) => r.data?.data || []),
      getTrainingApprovalSteps(id).then((r) => r.data?.data || []),
      getTrainingQuizConfig(id).then((r) => r.data?.data).catch(() => null),
      listTrainingQuizQuestions(id).then((r) => r.data?.data || []).catch(() => []),
    ]).then(([tr, ci, st, qc, qs]) => {
      setTraining(tr);
      setContent(ci);
      setSteps(st);
      setQuizConfig(qc);
      setQuestions(qs);
    }).finally(() => setLoading(false));
  }, [id]);

  const isCurrent = steps.some((s) => s.isCurrent);

  const handleApprove = useCallback(async () => {
    setActionLoading(true);
    try {
      await approveTraining(id, comment || undefined);
      history.push('/training-pending-approvals');
    } catch {} finally { setActionLoading(false); }
  }, [id, comment, history]);

  const handleRevision = useCallback(async () => {
    if (comment.trim().length < 10) return;
    setActionLoading(true);
    try {
      await requestTrainingRevision(id, comment);
      history.push('/training-pending-approvals');
    } catch {} finally { setActionLoading(false); setRevisionOpen(false); }
  }, [id, comment, history]);

  const handleReject = useCallback(async () => {
    if (comment.trim().length < 10) return;
    setActionLoading(true);
    try {
      await rejectTraining(id, comment);
      history.push('/training-pending-approvals');
    } catch {} finally { setActionLoading(false); setRejectOpen(false); }
  }, [id, comment, history]);

  const tabs = [
    { key: 'content', label: t('training.wizardStep2') },
    ...(training?.hasQuiz ? [{ key: 'quiz', label: t('training.wizardStep3') }] : []),
    { key: 'approval', label: t('training.wizardStep4') },
  ];

  if (loading) return <div style={{ padding: 24 }}>{t('common.loading')}</div>;
  if (!training) return <div style={{ padding: 24 }}>{t('training.notFound')}</div>;

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('menu.trainingPendingApprovals'), path: '/training-pending-approvals' },
          { label: training.title },
        ]} />

        <InfoCard>
          <TrainingTitle>{training.title}</TrainingTitle>
          <Badge>{t('training.statusPendingApproval')}</Badge>
          {training.categoryName && <span style={{ fontSize: 12, color: '#64748b' }}>{training.categoryName}</span>}
          <span style={{ fontSize: 12, color: '#64748b' }}>
            <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
            {training.durationMinutes} {t('training.minutesShort')}
          </span>
          {training.createUserFullName && (
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {t('training.createdBy')}: <b>{training.createUserFullName}</b>
            </span>
          )}
        </InfoCard>

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent>
        {activeTab === 'content' && (
          <>
            <Section>
              <SectionTitle>{t('training.contentItems')} ({content.length})</SectionTitle>
              {content.length === 0 ? (
                <div style={{ color: '#A0A8B4', fontSize: 13 }}>{t('training.noContentYet')}</div>
              ) : content.map((ci) => {
                const Icon = CONTENT_ICONS[ci.contentType] || FileText;
                const isActive = activeContent?.id === ci.id;
                return (
                  <ContentRow key={ci.id} $active={isActive} onClick={() => setActiveContent(isActive ? null : ci)}>
                    <Icon size={16} color={isActive ? '#0291EF' : '#3730A3'} />
                    <TypeBadge>{ci.contentType}</TypeBadge>
                    <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ci.originalFileName || ci.url}
                    </div>
                  </ContentRow>
                );
              })}
              {activeContent && (
                <PlayerArea>
                  <ContentPlayer
                    contentItem={activeContent}
                    disableFastForward={training?.disableFastForward === true}
                  />
                </PlayerArea>
              )}
            </Section>

            {training.shortDescription && (
              <Section>
                <SectionTitle>{t('training.shortDescription')}</SectionTitle>
                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{training.shortDescription}</div>
              </Section>
            )}
          </>
        )}

        {activeTab === 'quiz' && (
          <Section>
            {quizConfig && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16, fontSize: 13 }}>
                <div><span style={{ color: '#64748b' }}>{t('training.quizPassingScore')}:</span> %{quizConfig.passingScore}</div>
                <div><span style={{ color: '#64748b' }}>{t('training.quizMaxAttempts')}:</span> {quizConfig.maxAttempts || '∞'}</div>
                <div><span style={{ color: '#64748b' }}>{t('training.quizDurationMinutes')}:</span> {quizConfig.durationMinutes || '∞'}</div>
                <div><span style={{ color: '#64748b' }}>{t('training.quizQuestionCount')}:</span> {quizConfig.questionCount}</div>
              </div>
            )}
            <SectionTitle>{t('training.questionBank')} ({questions.length})</SectionTitle>
            {questions.map((q, i) => (
              <QuestionCard key={q.id} $color={typeColors[q.questionType]}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{i + 1}. {q.questionText}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                  {t(`training.questionType_${q.questionType}`)} · {t(`training.difficulty_${q.difficulty}`)}
                </div>
                {q.options?.map((o) => (
                  <div key={o.id} style={{ fontSize: 12, color: o.isCorrect ? '#16a34a' : '#64748b', padding: '1px 0' }}>
                    {o.isCorrect ? '✓' : '·'} {o.optionText}
                  </div>
                ))}
              </QuestionCard>
            ))}
          </Section>
        )}

        {activeTab === 'approval' && (
          <Section>
            <SectionTitle>{t('training.approvers')}</SectionTitle>
            {steps.map((s) => (
              <StepRow key={s.id}>
                <StepIndex>{s.orderIndex + 1}.</StepIndex>
                <span style={{ flex: 1, fontWeight: s.isCurrent ? 600 : 400 }}>{s.approverFullName || s.approverUserId}</span>
                {s.decision ? (
                  <DecisionBadge $d={s.decision}>
                    {s.decision === 'approved' ? t('training.approved') : s.decision === 'revision_requested' ? t('training.revisionRequested') : t('training.rejected')}
                  </DecisionBadge>
                ) : s.isCurrent ? (
                  <DecisionBadge $d={null}>{t('training.waitingApproval')}</DecisionBadge>
                ) : (
                  <span style={{ color: '#A0A8B4', fontSize: 12 }}>{t('training.pending')}</span>
                )}
                {s.decidedAt && <span style={{ fontSize: 11, color: '#94a3b8' }}>{formatDate(s.decidedAt, i18n.language)}</span>}
              </StepRow>
            ))}

            {steps.some((s) => s.comment) && (
              <div style={{ marginTop: 12 }}>
                {steps.filter((s) => s.comment).map((s) => (
                  <div key={s.id} style={{ background: '#F8FAFC', padding: 10, borderRadius: 6, marginBottom: 6, fontSize: 12 }}>
                    <span style={{ fontWeight: 600 }}>{s.approverFullName}:</span> {s.comment}
                  </div>
                ))}
              </div>
            )}

            {isCurrent && (
              <>
                <div style={{ marginTop: 16, borderTop: '1px solid #E0E4E8', paddingTop: 16 }}>
                  <TextInput
                    label={t('training.approvalComment')}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    textarea
                    rows={3}
                  />
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    {t('training.commentMandatoryHint')}
                  </div>
                </div>
                <ActionBar>
                  <Button onClick={handleApprove} disabled={actionLoading} style={{ fontSize: 13 }}>
                    <CheckCircle2 size={15} /> {t('training.approve')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRevisionOpen(true)}
                    disabled={actionLoading || comment.trim().length < 10}
                    style={{ fontSize: 13 }}
                  >
                    <RotateCcw size={15} /> {t('training.requestRevision')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setRejectOpen(true)}
                    disabled={actionLoading || comment.trim().length < 10}
                    style={{ fontSize: 13, color: '#dc2626' }}
                  >
                    <XCircle size={15} /> {t('training.reject')}
                  </Button>
                </ActionBar>
              </>
            )}
          </Section>
        )}
      </TabContent>

      <ConfirmDialog
        isOpen={revisionOpen}
        title={t('training.requestRevision')}
        message={t('training.revisionConfirm')}
        onConfirm={handleRevision}
        onCancel={() => setRevisionOpen(false)}
        confirmText={t('training.requestRevision')}
        type="info"
        loading={actionLoading}
      />
      <ConfirmDialog
        isOpen={rejectOpen}
        title={t('training.reject')}
        message={t('training.rejectConfirm')}
        onConfirm={handleReject}
        onCancel={() => setRejectOpen(false)}
        confirmText={t('training.reject')}
        loading={actionLoading}
      />
    </PageContainer>
  );
}
